import React from 'react';

import type { Nullable } from '@clearverify/types';

import {
  EmployersTableListDocument,
  useGetPeoBasicInfoQuery,
  useSendInvitesToEmployersUnderPeoMutation,
} from 'src/api';
import { FullscreenLoader } from 'src/components/common';
import { useNotification } from 'src/hooks/app';
import { useEmployersUnderPeoListQueryVariables } from 'src/hooks/employer';
import { TableDataProvider } from 'src/providers/TableDataProvider';
import { clearEntitiesData } from 'src/utils/bulkUpload';

import { EmployersUploadForm, EmployersUploadTable } from './components';
import { StepId } from './constants';
import { ExtendedInvitedEmployerData, InvitedEmployerData } from './types';
import { validateEmployers } from './utils/validateEmployers';

interface InviteEmployersUnderPeoModalContentProps {
  peoId: Nullable<string>;
  closeModal: () => void;
  switchClosability: (isClosureAllowed: boolean) => void;
}

export const InviteEmployersUnderPeoModalContent: React.FC<
  InviteEmployersUnderPeoModalContentProps
> = ({ peoId, closeModal, switchClosability }) => {
  const [currentStep, setCurrentStep] = React.useState<StepId>(StepId.Upload);

  const [employers, setEmployers] = React.useState<ExtendedInvitedEmployerData[]>([]);

  const { createApolloContext } = useNotification();

  const queryVariables = useEmployersUnderPeoListQueryVariables(peoId);

  const { data, loading: peoBasicInfoLoading } = useGetPeoBasicInfoQuery({
    variables: { id: peoId ?? '' },
    skip: !peoId,
  });

  const [sendInvites, { loading: sendInvitesLoading }] = useSendInvitesToEmployersUnderPeoMutation({
    context: createApolloContext({
      errorMessage: 'Failed to invite employers',
      successMessage: 'Employers have been successfully invited',
    }),
    refetchQueries: [{ query: EmployersTableListDocument, variables: queryVariables }],
  });

  const handleEmployersData = React.useCallback((employers: InvitedEmployerData[]) => {
    const validatedEmployers = validateEmployers(employers);
    setEmployers(validatedEmployers);
    setCurrentStep(StepId.Review);
  }, []);

  const handleSaveChanges = React.useCallback(async () => {
    if (!peoId) return;

    try {
      switchClosability(false);

      const validEmployers = employers.filter(employer => employer.valid);

      if (validEmployers.length > 0) {
        await sendInvites({
          variables: {
            input: { peoId, employers: clearEntitiesData(validEmployers) },
          },
        });
      }

      switchClosability(true);
      closeModal();
    } catch {
      switchClosability(true);
    }
  }, [closeModal, employers, peoId, sendInvites, switchClosability]);

  const handleCancel = React.useCallback(() => {
    closeModal();
  }, [closeModal]);

  const handleBack = React.useCallback(() => {
    setEmployers([]);
    setCurrentStep(StepId.Upload);
  }, []);

  const uploadFormTopNotice = `Using this modal, you can upload the file with Employers that should be added to the platform under the PEO ${data?.getPeoBasicInfo?.displayName ?? ''}. Please note that the Employers you add must be then set to Live status.`;

  const loading = sendInvitesLoading;

  if (peoBasicInfoLoading) {
    return <FullscreenLoader />;
  }

  if (currentStep === StepId.Upload) {
    return (
      <EmployersUploadForm
        handleEmployersData={handleEmployersData}
        handleCancel={handleCancel}
        switchClosability={switchClosability}
        nextStepLoading={loading}
        uploadFormTopNotice={uploadFormTopNotice}
      />
    );
  }

  return (
    <TableDataProvider>
      <EmployersUploadTable
        employers={employers}
        handleSaveChanges={handleSaveChanges}
        handleBack={handleBack}
        loading={sendInvitesLoading}
      />
    </TableDataProvider>
  );
};
